import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL, CountryOptions, UserRoles } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, Users, Search, Loader2, Award, Download } from 'lucide-react';
import { SortableHeader } from '../components/SortableHeader';
import { CompanyMultiPicker } from '../components/CompanyMultiPicker';
import { fetchCompaniesByIds } from '../lib/companies';
import { useNavigate, useSearchParams } from 'react-router-dom';

const Students = () => {
  usePageTitle('Students');
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50;
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Sort state
  const [sortBy, setSortBy] = useState(null); // null | 'first_name' | 'id_number' | 'email'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  
  const canExport = user?.role === UserRoles.ADMIN;
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    id_number: '',
    telephone: '',
    mobile: '',
    email: '',
    personal_email: '',
    email_permission: true,
    country: '',
    city: '',
    position: '',
    company_ids: [],
    training_center_ids: []
  });

  const fetchData = async (page = 1, search = '', sortByArg = sortBy, sortOrderArg = sortOrder) => {
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (search) params.append('search', search);
      if (sortByArg) {
        params.append('sort_by', sortByArg);
        params.append('sort_order', sortOrderArg);
      }
      
      const [studentsRes, centersRes] = await Promise.all([
        axios.get(`${API_URL}/persons?${params}`),
        axios.get(`${API_URL}/training-centers`)
      ]);

      // Handle paginated response
      const studentsData = studentsRes.data.items || studentsRes.data;
      setStudents(studentsData);
      setTotalPages(studentsRes.data.total_pages || 1);
      setTotalItems(studentsRes.data.total || studentsData.length);
      setCurrentPage(studentsRes.data.page || 1);

      // Only fetch the companies referenced by the visible students so we can
      // resolve their names in the table — no longer load all 4000+ companies.
      // Batched via helper to keep request URLs under proxy limits.
      const referencedIds = new Set();
      studentsData.forEach((s) => {
        (s.company_ids || []).forEach((id) => id && referencedIds.add(id));
        if (s.company_id) referencedIds.add(s.company_id);
      });
      const companiesList = await fetchCompaniesByIds(Array.from(referencedIds));
      setCompanies(companiesList);

      setTrainingCenters(centersRes.data);
      return studentsData;
    } catch (error) {
      toast.error('Failed to fetch data');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initData = async () => {
      const loadedStudents = await fetchData(1, '');
      
      // Check if there's an edit query parameter
      const editId = searchParams.get('edit');
      if (editId && loadedStudents.length > 0) {
        const studentToEdit = loadedStudents.find(s => s.id === editId);
        if (studentToEdit) {
          handleEdit(studentToEdit);
          // Clear the query param
          setSearchParams({});
        }
      }
    };
    initData();
  }, []);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        fetchData(1, searchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    fetchData(newPage, searchTerm);
  };

  // Click a column header to cycle: asc → desc → off
  const handleSort = (field) => {
    let nextBy = field;
    let nextOrder = 'asc';
    if (sortBy === field) {
      if (sortOrder === 'asc') {
        nextOrder = 'desc';
      } else {
        // Third click: clear sort
        nextBy = null;
        nextOrder = 'asc';
      }
    }
    setSortBy(nextBy);
    setSortOrder(nextOrder);
    setLoading(true);
    fetchData(1, searchTerm, nextBy, nextOrder);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      id_number: '',
      telephone: '',
      mobile: '',
      email: '',
      personal_email: '',
      email_permission: true,
      country: '',
      city: '',
      position: '',
      company_ids: [],
      training_center_ids: []
    });
    setCompanySearch('');
  };

  // Export students to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/persons/export/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'students_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${students.length} students to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export students');
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.id_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    
    try {
      const payload = { ...formData };
      // Convert empty strings to null for optional fields
      if (payload.email === '') payload.email = null;
      if (payload.personal_email === '') payload.personal_email = null;
      if (payload.telephone === '') payload.telephone = null;
      if (payload.mobile === '') payload.mobile = null;
      if (payload.country === '') payload.country = null;
      if (payload.city === '') payload.city = null;
      if (payload.position === '') payload.position = null;
      
      if (editingStudent) {
        await axios.put(`${API_URL}/persons/${editingStudent.id}`, payload);
        toast.success('Student updated successfully');
      } else {
        await axios.post(`${API_URL}/persons`, payload);
        toast.success('Student created successfully');
      }
      
      setDialogOpen(false);
      setEditingStudent(null);
      resetForm();
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save student';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setCompanySearch('');
    setFormData({
      first_name: student.first_name,
      last_name: student.last_name,
      id_number: student.id_number,
      telephone: student.telephone || '',
      mobile: student.mobile || '',
      email: student.email || '',
      personal_email: student.personal_email || '',
      email_permission: student.email_permission !== false,
      country: student.country || '',
      city: student.city || '',
      position: student.position || '',
      company_ids: student.company_ids || (student.company_id ? [student.company_id] : []),
      training_center_ids: student.training_center_ids || (student.training_center_id ? [student.training_center_id] : [])
    });
    setDialogOpen(true);
  };

  const handleDelete = async (student) => {
    setDeleteConfirm(student);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const student = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      await axios.delete(`${API_URL}/persons/${student.id}`);
      toast.success('Student deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    }
  };

  const handleCreateUserAccount = async (student) => {
    if (!student.email) {
      toast.error('Student must have an email address to create a user account');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/persons/${student.id}/create-user`);
      toast.success(response.data.message, {
        description: `Login credentials sent to ${student.email}. Initial password is their ID number.`
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user account');
    }
  };

  // Build O(1) lookup map for company names — used by table rows
  const companyMap = React.useMemo(() => {
    const m = new Map();
    companies.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [companies]);

  const getCompanyName = (companyId) => companyMap.get(companyId) || '-';

  const getTrainingCenterName = (centerId) => {
    const center = trainingCenters.find(c => c.id === centerId);
    return center?.name || '-';
  };

  const getCompanyNames = (companyIds) => {
    if (!companyIds || companyIds.length === 0) return '-';
    return companyIds.map(id => companyMap.get(id) || '').filter(Boolean).join(', ') || '-';
  };

  // When using server-side pagination, use students directly (filtering is done on server)
  const filteredStudents = students;

  if (loading && students.length === 0) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="students-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Students
          </h1>
          <p className="text-slate-500 mt-1">
            Manage trainees and their information
          </p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button 
              variant="outline"
              onClick={handleExportCSV}
              disabled={exporting || students.length === 0}
              data-testid="export-students-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          )}
          <Button 
            className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
            onClick={() => {
              setEditingStudent(null);
              resetForm();
              setDialogOpen(true);
            }}
            data-testid="add-student-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Student
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, ID number, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="student-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Students Table */}
      <Card>
        <CardContent className="p-0">
          {filteredStudents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No students found</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm ? 'Try a different search term' : 'Get started by adding your first student'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <SortableHeader field="first_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-name">Name</SortableHeader>
                  <SortableHeader field="id_number" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-id">ID Number</SortableHeader>
                  <SortableHeader field="email" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-email">Email</SortableHeader>
                  <TableHead className="font-semibold">Mobile</TableHead>
                  <TableHead className="font-semibold">Company</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStudents.map((student) => (
                  <TableRow 
                    key={student.id} 
                    className="hover:bg-slate-50/50 cursor-pointer" 
                    data-testid={`student-row-${student.id}`}
                    onClick={() => navigate(`/students/${student.id}`)}
                  >
                    <TableCell className="font-medium text-[#2C9DE1] hover:underline">
                      {student.first_name} {student.last_name}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{student.id_number}</TableCell>
                    <TableCell>{student.email || '-'}</TableCell>
                    <TableCell>{student.mobile || '-'}</TableCell>
                    <TableCell>{getCompanyNames(student.company_ids || (student.company_id ? [student.company_id] : []))}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`student-actions-${student.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/students/${student.id}`)}>
                            <Award className="w-4 h-4 mr-2" />
                            View Certificates
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(student)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(student)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-slate-500">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems} students
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingStudent ? 'Edit Student' : 'Add New Student'}
            </DialogTitle>
            <DialogDescription>
              {editingStudent ? 'Update student details' : 'Add a new trainee to the system'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="form-section-header">Personal Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                    data-testid="student-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Doe"
                    data-testid="student-lastname-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="id_number">ID Number *</Label>
                <Input
                  id="id_number"
                  value={formData.id_number}
                  onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                  placeholder="e.g., CY12345678"
                  data-testid="student-idnumber-input"
                />
                <p className="text-xs text-slate-500">
                  This will be used as the initial password for the student&apos;s account
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="form-section-header">Contact Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telephone">Telephone</Label>
                  <Input
                    id="telephone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    placeholder="+357 22 123456"
                    data-testid="student-telephone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile</Label>
                  <Input
                    id="mobile"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="+357 99 123456"
                    data-testid="student-mobile-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Work Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john.doe@company.com"
                    data-testid="student-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="personal_email">Personal Email</Label>
                  <Input
                    id="personal_email"
                    type="email"
                    value={formData.personal_email}
                    onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })}
                    placeholder="john.doe@gmail.com"
                    data-testid="student-personalemail-input"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="email_permission"
                  checked={formData.email_permission}
                  onCheckedChange={(checked) => setFormData({ ...formData, email_permission: checked })}
                  data-testid="student-emailpermission-checkbox"
                />
                <Label htmlFor="email_permission" className="text-sm font-normal">
                  Permission to send promotional emails
                </Label>
              </div>
            </div>

            {/* Location & Company */}
            <div className="space-y-4">
              <h3 className="form-section-header">Location & Company</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={formData.country || "none"}
                    onValueChange={(value) => setFormData({ ...formData, country: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="student-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select country</SelectItem>
                      {CountryOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Nicosia"
                    data-testid="student-city-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="position">Position</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder="e.g., Safety Officer, Nurse, Manager"
                  data-testid="student-position-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Companies</Label>
                <CompanyMultiPicker
                  value={formData.company_ids}
                  onChange={(ids) => setFormData({ ...formData, company_ids: ids })}
                  selectedCompanies={formData.company_ids
                    .map((id) => companies.find((c) => c.id === id))
                    .filter(Boolean)}
                />
              </div>

              <div className="space-y-2">
                <Label>Training Centers</Label>
                <div className="border rounded-lg p-3 max-h-[150px] overflow-y-auto space-y-2">
                  {trainingCenters.length === 0 ? (
                    <p className="text-sm text-slate-500">No training centers available</p>
                  ) : (
                    trainingCenters.map((center) => (
                      <div key={center.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`center-${center.id}`}
                          checked={formData.training_center_ids.includes(center.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, training_center_ids: [...formData.training_center_ids, center.id] });
                            } else {
                              setFormData({ ...formData, training_center_ids: formData.training_center_ids.filter(id => id !== center.id) });
                            }
                          }}
                        />
                        <Label htmlFor={`center-${center.id}`} className="text-sm font-normal cursor-pointer">
                          {center.name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={saving}
                data-testid="student-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingStudent ? 'Update Student' : 'Create Student'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Delete Student
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteConfirm.first_name} {deleteConfirm.last_name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Students;
